import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { portalAPI, adminAPI } from '../services/api';
import { getPortalOptions, updatePortalOptions } from '../services/portalOptions';
import { useAuth } from '../context/AuthContext';

const PortalSettings = () => {
  const { portalId } = useParams();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.permission_level === 'global_admin';
  const [settings, setSettings] = useState([]);
  const [portal, setPortal] = useState(null);
  const [samlEnabled, setSamlEnabled] = useState(false);
  const [ldapEnabled, setLdapEnabled] = useState(false);
  const [authSaveLoading, setAuthSaveLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('portal'); // 'portal' or 'user'
  const [newSetting, setNewSetting] = useState({ name: '', value: '' });
  const [editMode, setEditMode] = useState(false);
  const [currentEditSetting, setCurrentEditSetting] = useState(null);

  useEffect(() => {
    loadPortalSettings();
    if (activeTab === 'portal') {
      loadPortalOptions();
    }
  }, [portalId, activeTab]);

  // Load SAML/LDAP options
  const loadPortalOptions = async () => {
    setOptionsLoading(true);
    try {
      const data = await getPortalOptions(portalId);
      setSamlEnabled(!!data.enable_saml);
      setLdapEnabled(!!data.enable_ldap);
    } catch (err) {
      setError(err.message || 'Failed to load portal options');
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadPortalSettings = async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      if (activeTab === 'portal') {
        response = await adminAPI.getPortalSettings(portalId);
      } else {
        response = await portalAPI.getPortalSettings(portalId);
      }
      if (response.status === 'error') throw new Error(response.error || 'Failed to load settings');
      setSettings(response.settings || []);
      if (response.portal) {
        setPortal(response.portal);
        setSamlEnabled(!!response.portal.enable_saml);
        setLdapEnabled(!!response.portal.enable_ldap);
      } else {
        console.warn('No portal object in response:', response);
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setEditMode(false);
    setCurrentEditSetting(null);
    setNewSetting({ name: '', value: '' });
    setSuccess('');
    setError('');
  };

  const handleNewSettingChange = (field, value) => {
    setNewSetting((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSettingChange = (field, value) => {
    setCurrentEditSetting((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let response;
      if (editMode && currentEditSetting) {
        if (activeTab === 'portal') {
          response = await adminAPI.updatePortalSetting(portalId, currentEditSetting.id, currentEditSetting.value);
        } else {
          response = await portalAPI.updatePortalSetting(portalId, currentEditSetting.id, currentEditSetting.value);
        }
        if (response.status === 'success') {
          setSuccess(`Setting "${currentEditSetting.name}" updated successfully`);
          setEditMode(false);
          setCurrentEditSetting(null);
          loadPortalSettings();
        } else {
          setError(response.error || 'Failed to update setting');
        }
      } else if (activeTab === 'user' || activeTab === 'portal') {
      if (!newSetting.name || !newSetting.value) {
        setError('Both name and value are required.');
        setSaving(false);
        return;
      }
      const existing = settings.find((s) => s.name === newSetting.name);
      if (existing) {
        if (activeTab === 'portal') {
          response = await adminAPI.updatePortalSetting(portalId, existing.id, newSetting.value);
        } else {
          response = await portalAPI.updatePortalSetting(portalId, existing.id, newSetting.value);
        }
      } else {
        if (activeTab === 'portal') {
          response = await adminAPI.createPortalSetting(portalId, newSetting.name, newSetting.value);
        } else {
          if (!newSetting.name || !newSetting.value) {
            console.error('Blocked submission: Missing name or value', newSetting);
            setError('Both name and value are required for a user setting.');
            setSaving(false);
            return;
          }
          const payload = [{ name: newSetting.name, value: newSetting.value }];
          response = await portalAPI.updatePortalSettings(portalId, payload);
        }
      }
      if (response.status === 'success') {
        setSuccess(`Setting "${newSetting.name}" added/updated successfully`);
        setNewSetting({ name: '', value: '' });
        loadPortalSettings();
      } else {
        setError(response.error || 'Failed to add/update setting');
      }
    }
    } catch (err) {
      setError(err.message || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSetting = async (id, name) => {
    if (!window.confirm(`Delete setting "${name}"?`)) return;
    setError('');
    setSuccess('');
    try {
      let response;
      if (activeTab === 'portal') {
        response = await adminAPI.deletePortalSetting(portalId, id);
      } else {
        response = await portalAPI.deletePortalSetting(portalId, id);
      }
      if (response.status === 'success') {
        setSuccess(`Setting "${name}" deleted successfully`);
        loadPortalSettings();
      } else {
        setError(response.error || 'Failed to delete setting');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete setting');
    }
  };

  const startEditSetting = (setting) => {
    setCurrentEditSetting(setting);
    setEditMode(true);
    setSuccess('');
    setError('');
  };

  const cancelEdit = () => {
    setEditMode(false);
    setCurrentEditSetting(null);
    setSuccess('');
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Handler to save SAML/LDAP toggles
  const handleAuthOptionsSave = async () => {
    setAuthSaveLoading(true);
    setError("");
    setSuccess("");
    try {
      const resp = await updatePortalOptions(portalId, {
        enable_saml: samlEnabled,
        enable_ldap: ldapEnabled,
      });
      if (resp.status === "success") {
        setSuccess("Authentication options updated successfully");
        loadPortalOptions();
      } else {
        setError(resp.error || "Failed to update authentication options");
      }
    } catch (err) {
      setError(err.message || "Failed to update authentication options");
    } finally {
      setAuthSaveLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {isAdmin && (
              <button
                onClick={() => handleTabChange('portal')}
                className={`${activeTab === 'portal' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Portal Settings
              </button>
            )}
            <button
              onClick={() => handleTabChange('user')}
              className={`${activeTab === 'user' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              User Settings
            </button>
          </nav>
        </div>
        {portal && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{portal.name}</h1>
            <p className="mt-2 text-gray-600">{portal.description}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}
        {/* SAML/LDAP Auth Options - Only on Portal tab */}
        {isAdmin && activeTab === 'portal' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8 p-6">
            <h4 className="text-lg font-semibold mb-2">Authentication Options</h4>
            {optionsLoading ? (
              <div className="text-gray-500">Loading authentication options...</div>
            ) : (
              <>
                <div className="flex items-center space-x-6 mb-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={samlEnabled}
                      onChange={e => setSamlEnabled(e.target.checked)}
                      className="form-checkbox h-5 w-5 text-primary"
                      disabled={authSaveLoading}
                    />
                    <span>Enable SAML Authentication</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={ldapEnabled}
                      onChange={e => setLdapEnabled(e.target.checked)}
                      className="form-checkbox h-5 w-5 text-primary"
                      disabled={authSaveLoading}
                    />
                    <span>Enable LDAP Authentication</span>
                  </label>
                  <button
                    onClick={handleAuthOptionsSave}
                    disabled={authSaveLoading}
                    className={`ml-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${authSaveLoading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-hover'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                  >
                    {authSaveLoading ? 'Saving...' : 'Save Auth Options'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Toggling these options will enable or disable SAML/LDAP authentication for this portal.</p>
              </>
            )}
          </div>
        )}

        {/* Settings List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {activeTab === 'portal' ? 'Portal Settings' : 'User Settings'}
            </h3>
            {isAdmin && activeTab === 'portal' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-light text-primary">
                Admin Access
              </span>
            )}
          </div>
          {settings.length === 0 ? (
            <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
              No settings found. Add a new setting below.
            </div>
          ) : (
            <div className="border-t border-gray-200">
              <dl>
                {settings.map((setting, idx) => (
                  <div key={setting.id || idx} className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 relative`}>
                    <dt className="text-sm font-medium text-gray-500">{setting.name}</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex justify-between items-center">
                      {editMode && currentEditSetting?.id === setting.id ? (
                        <input
                          type="text"
                          className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                          value={currentEditSetting.value}
                          onChange={(e) => handleEditSettingChange('value', e.target.value)}
                        />
                      ) : (
                        <span>{setting.value}</span>
                      )}
                      <div className="flex space-x-2">
                        {editMode && currentEditSetting?.id === setting.id ? (
                          <>
                            <button
                              type="button"
                              onClick={handleSubmit}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => startEditSetting(setting)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-primary bg-primary-light hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSetting(setting.id, setting.name)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </dd>
                    {setting.created_at && (
                      <div className="col-span-3 text-xs text-gray-500 mt-1">
                        Created: {new Date(setting.created_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
        {/* Add/Edit Setting Form */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {editMode ? 'Edit Setting' : 'Add New Setting'}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {editMode ? 'Update the value for this setting.' : 'Create a new setting for this portal.'}
            </p>
          </div>
          <div className="border-t border-gray-200">
            <form key={activeTab} onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="setting-name" className="block text-sm font-medium text-gray-700">
                    Setting Name
                  </label>
                  <input
                    type="text"
                    name="setting-name"
                    id="setting-name"
                    value={editMode ? currentEditSetting?.name : newSetting.name}
                    onChange={(e) => editMode ? null : handleNewSettingChange('name', e.target.value)}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                    disabled={editMode}
                  />
                </div>
                <div>
                  <label htmlFor="setting-value" className="block text-sm font-medium text-gray-700">
                    Setting Value
                  </label>
                  <input
                    type="text"
                    name="setting-value"
                    id="setting-value"
                    value={editMode ? currentEditSetting?.value : newSetting.value}
                    onChange={(e) => editMode ? handleEditSettingChange('value', e.target.value) : handleNewSettingChange('value', e.target.value)}
                    className="mt-1 focus:ring-primary focus:border-primary block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={saving || (editMode ? !currentEditSetting?.value : !newSetting.name || !newSetting.value)}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${saving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-hover'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                >
                  {saving ? (editMode ? 'Saving...' : 'Adding...') : (editMode ? 'Save' : 'Add Setting')}
                </button>
                {editMode && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="ml-4 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalSettings;